import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PrivacyBadge from './PrivacyBadge';
import FeedbackBar from './FeedbackBar';
import PastValidationModal from './PastValidationModal';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const CHAT_HISTORY_KEY = 'mingpan_zhi_chat_history_v2';

function ZhiChatModal({ isOpen, onClose, baziData, dayunData }) {
  const navigate = useNavigate();
  const { canChat, getZhiChatRemaining, getZhiRoundInfo, incrementZhiChat, isMember, isSingle, isFree, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // ========== v4.0 前事验证 + 反馈 ==========
  const [showValidation, setShowValidation] = useState(false);
  const [validationComplete, setValidationComplete] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showEndFeedback, setShowEndFeedback] = useState(false);
  const [endRating, setEndRating] = useState(0);
  const [endTopics, setEndTopics] = useState([]);
  const [endSuggestion, setEndSuggestion] = useState('');
  const [sessionId] = useState(() => `zhi_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`);

  // 每次打开弹窗时，尝试恢复历史记录
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // 如果当前已无法对话（限额用完），显示限额提示
      if (!canChat()) {
        setLimitReached(true);
        return;
      }

      // 计算命盘指纹，判断是否需要前事验证
      const year = baziData?.input_calendar_date?.year || '';
      const month = baziData?.input_calendar_date?.month || '';
      const day = baziData?.input_calendar_date?.day || '';
      const hour = baziData?.input_calendar_date?.hour || '';
      const baziFingerprint = `${year}|${month}|${day}|${hour}`;
      const validatedFingerprint = localStorage.getItem('zhi_validated_bazi');

      const alreadyValidated = validatedFingerprint === baziFingerprint;
      if (alreadyValidated) {
        setValidationComplete(true);
        setShowValidation(false);
      } else {
        setValidationComplete(false);
        setShowValidation(true);
      }

      // 尝试恢复之前的聊天记录（仅同命盘）
      try {
        const saved = localStorage.getItem(CHAT_HISTORY_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          const savedDM = parsed.day_master || '';
          const currentDM = baziData?.day_master || '';
          if (savedDM === currentDM && Array.isArray(parsed.messages) && parsed.messages.length > 0) {
            setMessages(parsed.messages);
            // 如果历史记录中验证已完成，沿用
            if (parsed.validationComplete) {
              setValidationComplete(true);
              setValidationResult(parsed.validationResult || null);
              setShowValidation(false);
            }
            return; // 有历史记录，跳过开场白
          }
        }
      } catch {}

      // 没有历史记录，生成开场白
      const dayMaster = baziData?.day_master || '';

      const greetings = [
          `您好！我是知知先生，您的八字命理顾问。\n\n您的八字命盘已排好，四柱为【${year}年 ${month}月 ${day}日 ${hour}时】${dayMaster ? `，日主${dayMaster}` : ''}。\n\n我可以为您解读：\n• 八字格局与五行喜忌\n• 事业方向与职业建议\n• 财运走势与理财时机\n• 感情婚姻与正缘分析\n• 流年大运与吉凶预测\n• 开运方法与风水建议\n\n请问您想了解哪方面？`,
          `又见面了。知知在此，愿为您指点迷津。\n\n您的命盘四柱【${year}年 ${month}月 ${day}日 ${hour}时】，我已了然于心。\n\n今日想聊些什么？事业、财运、感情，或是其他的困惑，但说无妨。`,
          `知知先生给您请安了。\n\n您的八字【${year}年 ${month}月 ${day}日 ${hour}时】已排定，随时恭候您的提问。\n\n命理之道，玄妙无穷。无论您关心哪一方面，我都愿意为您细细道来。`,
          `🦊 知知在此，以命理之智，解您心中之惑。\n\n我已看过您的命盘，${dayMaster ? `日主为${dayMaster}，` : ''}四柱【${year}年 ${month}月 ${day}日 ${hour}时】。\n\n古人云：知命者不怨天，知己者不尤人。让我们从您的命盘开始，聊聊您关心的事？`,
          `您好，知知先生已恭候多时。\n\n您的八字为【${year}年 ${month}月 ${day}日 ${hour}时】，我已为您排好命盘。\n\n今日运势、事业发展、感情走向、财富机遇……您想先了解哪一个？`,
        ];

        // 按日主固定匹配开场白风格：同一八字永远是同一种语气
        const DAY_MASTER_LIST = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
        const idx = DAY_MASTER_LIST.indexOf(dayMaster);
        const greeting = greetings[idx >= 0 ? idx % greetings.length : 0];

        setMessages([{ role: 'assistant', content: greeting, id: Date.now() }]);
    }
  }, [isOpen, baziData]);

  // 对话更新时自动保存到 localStorage
  useEffect(() => {
    if (messages.length > 0) {
      const dm = baziData?.day_master || '';
      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify({
        day_master: dm,
        messages: messages,
        validationComplete: validationComplete,
        validationResult: validationResult,
        updatedAt: new Date().toISOString()
      }));
    }
  }, [messages, validationComplete, validationResult]);

  // 滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // 自动聚焦
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

    const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    // 检查对话次数（终身限制）
    if (!canChat()) {
      setLimitReached(true);
      // 会话结束，清除本地历史，下次打开时重新开始
      localStorage.removeItem(CHAT_HISTORY_KEY);
      return;
    }

    const userMsg = { role: 'user', content: text, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setStreamingContent('');

    // 构建历史
    const history = messages.map(m => ({ role: m.role, content: m.content }));

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await fetch(`${API_BASE}/api/zhi/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          message: text,
          bazi_data: baziData,
          history: history,
          dayun_data: dayunData || null
        }),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // 使用 response.text() 替代 ReadableStream reader（兼容性更好）
      const raw = await response.text();
      let fullContent = '';

      // 检查是否为 JSON 错误响应（非 SSE 格式），如访客体验用完等
      if (raw.trim().startsWith('{')) {
        try {
          const jsonResp = JSON.parse(raw);
          if (!jsonResp.success && jsonResp.data) {
            const errData = jsonResp.data;
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: errData.body || jsonResp.message || '对话次数已用完',
              id: Date.now(),
              isSystem: true,
              errorCode: errData.code,
              cta_single: errData.cta_single,
              cta_quarterly: errData.cta_quarterly,
            }]);
            setLoading(false);
            return;
          }
        } catch {
          // 不是 JSON，继续按 SSE 处理
        }
      }

      // 手动解析 SSE 格式
      const lines = raw.split('\n');
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        const data = trimmed.slice(6).trim();
        if (data === '[DONE]') continue;
        if (data.startsWith('[ERROR]')) {
          throw new Error(data.slice(8));
        }
        try {
          const parsed = JSON.parse(data);
          if (parsed.content) {
            fullContent += parsed.content;
          }
        } catch {
          // 非JSON内容（如 validation 事件），跳过
        }
      }

      // 添加到消息列表
      if (fullContent) {
        // 模拟流式打字效果
        for (let i = 1; i <= fullContent.length; i += 2) {
          if (abortRef.current?.signal?.aborted) break;
          setStreamingContent(fullContent.slice(0, i));
          await new Promise(r => setTimeout(r, 10));
        }
        setStreamingContent('');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: fullContent,
          id: Date.now()
        }]);
      } else {
        setStreamingContent('');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '抱歉，刚才想说的好像没说出来，要不你再问一次？',
          id: Date.now()
        }]);
      }
      
      // 消耗终身对话次数（非会员、API 成功收到回复后）
      incrementZhiChat();
    } catch (err) {
      if (err.name === 'AbortError') {
        // 用户主动取消
        if (streamingContent) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: streamingContent,
            id: Date.now()
          }]);
        }
      } else {
        console.error('知知先生请求失败:', err);
        // 回退到本地规则引擎
        const fallback = generateLocalResponse(text, baziData);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: fallback,
          id: Date.now()
        }]);
      }
      setStreamingContent('');
    }
    setLoading(false);
    abortRef.current = null;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStop = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  };

  // 前事验证完成回调
  const handleValidationComplete = useCallback((result) => {
    setValidationComplete(true);
    setValidationResult(result);
    setShowValidation(false);
    // 记住已验证的命盘，下次相同命盘跳过验证
    const year = baziData?.input_calendar_date?.year || '';
    const month = baziData?.input_calendar_date?.month || '';
    const day = baziData?.input_calendar_date?.day || '';
    const hour = baziData?.input_calendar_date?.hour || '';
    localStorage.setItem('zhi_validated_bazi', `${year}|${month}|${day}|${hour}`);
  }, [baziData]);

  // 关闭时的处理 - 触发结束反馈
  const handleClose = () => {
    // 如果有对话历史且不是首次打开，触发结束反馈
    if (messages.length > 1 && validationComplete) {
      setShowEndFeedback(true);
      return;
    }
    onClose();
  };

  // 提交结束反馈
  const handleEndFeedbackSubmit = async () => {
    try {
      await fetch(`${API_BASE}/api/zhi/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          feedback_type: 'end_session',
          rating: endRating,
          interested_topics: endTopics,
          open_suggestion: endSuggestion || null,
        }),
      });
    } catch {}
    setShowEndFeedback(false);
    onClose();
  };

  const skipEndFeedback = () => {
    setShowEndFeedback(false);
    onClose();
  };

  // ========== 本地回退回答（无API时） ==========
  const generateLocalResponse = (text, data) => {
    const q = text.toLowerCase();
    const pattern = data?.pattern || '';
    const favorable = data?.favorable_elements || [];
    const strength = data?.day_master_strength || '中和';
    const dm = data?.day_master || '';

    if (q.includes('事业') || q.includes('工作') || q.includes('创业') || q.includes('职业')) {
      return `从你的八字来看，你的命格是${pattern}，${
        pattern.includes('食伤') ? '适合创意型和需要表达能力的职业，比如设计、写作、教育、新媒体这类。不过食伤格的人想法多，容易分心，要特别注意选好一个方向坚持下去。' :
        pattern.includes('官杀') ? '恭喜你，这种格局的人在管理和组织方面有天然优势。公务员、企业管理、项目经理这些方向特别适合。但别把自己逼太紧，你容易对人对己都要求太高。' :
        pattern.includes('财') ? '金融、贸易、销售这些跟钱打交道的领域适合你。你对数字敏感，知道什么生意能赚钱。建议先在大平台积累资源和人脉，时机成熟了再考虑自己干。' :
        pattern.includes('印') ? '教育、研究、写作、咨询这类需要专业深度的工作最适合你。找个你真正感兴趣的领域深耕下去，会很有成就感。' :
        '建议多尝试几个方向，找到让你有心流体验的那个。八字虽然没有特别突出的方向性，但这反而给了你自由选择的空间。'
      }你现在是${strength}，${
        strength === '身强' ? '精力够用，可以同时推进多个事情。但注意别太分散。' :
        '精力有限，要惜力。别什么都想要，挑最重要的那件事全力去做。'
      }`;
    }

    if (q.includes('感情') || q.includes('恋爱') || q.includes('婚姻') || q.includes('对象')) {
      return `感情的事啊，八字看得出一部分，但真正要走到一起还是靠两个人的经营。你的日主是${dm}，${
        pattern.includes('食伤') ? '其实你感情挺丰富的，但不太会跟对方表达心里的感受。有时候心里翻江倒海，说出来就变成冷冷淡淡的一句话。学着把真感受说出来，别让人猜。' :
        pattern.includes('官杀') ? '你在感情里很认真，想要承诺和稳定。但有时候想得太多，反而错过了开口的时机。别怕，心动的时候就大胆一点。' :
        pattern.includes('财') ? '你比较看重现实生活的和谐，是踏实可靠的伴侣。不过偶尔也可以浪漫一下，给对方一些惊喜。' :
        pattern.includes('印') ? '你是个深情的人，善于倾听和包容，会是很靠谱的另一半。不过别太被动，感情也需要你主动去表达和争取。' :
        '感情方面比较平稳，需要双方多沟通多经营。'
      }别着急，好缘分不怕晚。`;
    }

    if (q.includes('财运') || q.includes('赚钱') || q.includes('财富') || q.includes('投资')) {
      return `说到财运，你的喜用神是${favorable.join('、')}，对你有利的五行属性是${
        favorable.join('和')
      }。可以重点关注这些五行的行业和投资方向。${
        pattern.includes('财') ? '你的八字带财，对金钱敏感，是能赚到钱的人。但要注意别太贪快钱，稳扎稳打是最好的策略。' :
        '财运整体平稳，不必急于一时。小步快跑，慢慢积累就好。'
      }记住一点：我虽然能帮你看看运势方向，但具体的投资决策还是要你自己拿主意，尤其是涉及数额较大的时候。`;
    }

    if (q.includes('健康') || q.includes('身体') || q.includes('生病')) {
      return `健康方面，${
        strength === '身强' ? '你体质底子不错，但容易因为精力太旺盛而过度消耗。要注意劳逸结合，别等到身体亮红灯了才想到休息。' :
        strength === '身弱' ? '你的体质偏弱一些，容易累，需要多注意保养。规律作息、适当运动、吃好三顿饭，这些看似简单的事对你特别重要。' :
        '整体健康状态还不错，保持规律的生活习惯就好。'
      }五行上你需要注意${favorable.join('和')}对应的身体部位。当然，有不舒服一定要先去看医生，八字只能帮你多一分警惕，不能代替医学诊断。`;
    }

    if (q.includes('建议') || q.includes('方向') || q.includes('怎么办')) {
      return `看你问的这个，应该是遇到需要做选择的时候了。从你的八字来看，最有利的方向是跟${favorable.join('、')}相关的事情。${
        strength === '身强' ? '你现在能量够用，可以大胆一些，多去尝试。就算试错了也不怕，你还扛得住。' :
        '眼下不适合做太大的变动，先稳住再说。把眼前的事情做好，路走着走着就宽了。'
      }关鍵是跟着自己的节奏走，别跟别人比，拿着自己的八字走别人的路反而容易出问题。`;
    }

    // 默认回答
    return `我明白你的问题了。从你的八字来看，你的命格是${pattern}，日主${dm}的状态是${strength}。对你来说，${
      favorable.length > 0 ? `最有利的能量方向是${favorable.join('和')}，不管是工作、生活还是人际关系，多往这些方向靠会有帮助。` : ''
    }不过我刚才说的只是一个大的方向，要不你再具体说说你想问哪个方面？比如事业、感情、财运，或者最近遇到的什么具体的事？`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex flex-col bg-white"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white/95 backdrop-blur-sm sticky top-0 z-10"
          style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-100 flex items-center justify-center shadow-sm">
              <span className="text-xl sm:text-2xl">🦊</span>
            </div>
            <div>
              <h2 className="font-bold text-ink text-base sm:text-lg leading-tight">知知先生</h2>
              <p className="text-[10px] sm:text-xs text-stone">智慧长者 · 知心命理顾问</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isMember && (() => {
              const remaining = getZhiChatRemaining();
              let label = isFree ? `免费 · 剩余 ${remaining} 次` : `剩余 ${remaining} 次`;
              return (
                <span className="text-[10px] text-stone/40 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  {label}
                </span>
              );
            })()}
            {isMember && (() => {
              const remaining = getZhiChatRemaining();
              return (
                <span className="text-[10px] text-amber-600/60 bg-amber-50 px-1.5 py-0.5 rounded-full">
                  会员 · 剩余 {remaining}
                </span>
              );
            })()}
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center text-stone"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50">
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-orange-50 flex-shrink-0 mr-2 flex items-center justify-center text-sm self-end">
                  🦊
                </div>
              )}
              <div
                className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-zhusha text-white rounded-br-md'
                    : 'bg-white text-ink rounded-bl-md shadow-sm border border-gray-100'
                }`}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
                {msg.role === 'assistant' && msg.id !== messages[0]?.id && validationComplete && (
                  <FeedbackBar
                    messageContent={msg.content}
                    sessionId={sessionId}
                  />
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0 ml-2 flex items-center justify-center text-sm self-end">
                  👤
                </div>
              )}
            </motion.div>
          ))}

          {/* Streaming message */}
          {streamingContent && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-orange-50 flex-shrink-0 mr-2 flex items-center justify-center text-sm self-end">
                🦊
              </div>
              <div className="max-w-[80%] px-4 py-3 rounded-2xl bg-white text-ink rounded-bl-md shadow-sm border border-gray-100">
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {streamingContent}
                  <span className="inline-block w-1.5 h-4 bg-zhusha ml-0.5 animate-pulse rounded-sm align-middle" />
                </div>
              </div>
            </motion.div>
          )}

          {/* Loading indicator (initial, before streaming starts) */}
          {loading && !streamingContent && (
            <div className="flex justify-start">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-orange-50 flex-shrink-0 mr-2 flex items-center justify-center text-sm self-end">
                🦊
              </div>
              <div className="bg-white px-5 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 bg-amber-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          {/* 快捷提问：仅在首次打开且只有开场白时显示 */}
          {messages.length === 1 && !loading && canChat() && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="px-4 space-y-2"
            >
              <p className="text-[10px] text-stone/40 text-center">试试这些问题：</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['我适合做什么工作？', '最近的财运怎么样？', '我的感情运势如何？', '帮我看看健康状况'].map((q) => (
                  <button
                    key={q}
                    onClick={() => { setInput(q); inputRef.current?.focus(); }}
                    className="px-3 py-1.5 bg-white border border-amber-200 rounded-full text-xs text-amber-700 hover:bg-amber-50 hover:border-amber-300 transition-all active:scale-95"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 限额用完：输入区域替换为会员引导卡（不可关闭，避免重复弹出） */}
        {limitReached ? (
          <div className="px-4 py-4 border-t border-gray-100 bg-white"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 max-w-2xl mx-auto"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">🦊</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    {isFree ? '免费次数已用完' : '次数已用完'}
                  </p>
                  <p className="text-xs text-amber-600 mt-1 leading-relaxed">
                    {isFree && '3 次免费体验已用完，但知知先生还可以帮你：'}
                    {isMember && '会员次数已用完，可以购买对话包继续聊。'}
                  </p>
                  {isFree && (
                    <ul className="mt-1.5 space-y-0.5">
                      {['解读八字格局与五行喜忌', '分析事业财运走势', '婚姻感情与正缘分析', '流年大运吉凶预测'].map((item, i) => (
                        <li key={i} className="text-xs text-amber-700/70 flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-amber-400 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => { onClose(); navigate('/membership'); }}
                      className="px-3 py-2 bg-white text-amber-700 text-xs rounded-lg font-medium border border-amber-300 hover:bg-amber-100 transition-all"
                    >
                      体验包 ¥9.9
                    </button>
                    <button
                      onClick={() => { onClose(); navigate('/membership'); }}
                      className="px-3 py-2 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs rounded-lg font-medium shadow-md hover:shadow-lg transition-all"
                    >
                      开通季度会员 ¥38.8
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        ) : (
          /* Input area */
          <div className="px-4 py-3 border-t border-gray-100 bg-white"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
            <div className="flex items-end gap-2 max-w-2xl mx-auto">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isMember ? "有什么想问知知先生的？" : canChat() ? (isFree ? "这是你的免费体验，有什么想问的？" : "有什么想问知知先生的？") : "升级会员后继续对话"}
                  rows={1}
                  className="w-full px-4 py-2.5 pr-10 bg-gray-50 rounded-2xl text-sm text-ink placeholder-gray-400 resize-none border border-gray-200 focus:border-zhusha/30 focus:ring-2 focus:ring-zhusha/10 focus:outline-none transition-all"
                  disabled={loading || !canChat()}
                  style={{ minHeight: '42px', maxHeight: '120px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                />
              </div>
              {loading ? (
                <button
                  onClick={handleStop}
                  className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors flex items-center justify-center flex-shrink-0"
                >
                  <span className="text-sm">⏹</span>
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || !canChat()}
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                    input.trim() && canChat()
                      ? 'bg-zhusha text-white hover:bg-zhusha/90 shadow-md'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              )}
            </div>
            <div className="space-y-2">
              <PrivacyBadge type="ai_feature" compact />
              <p className="text-center text-xs text-gray-400">
                知知先生的回答基于八字命盘分析，仅供参考，不构成专业建议
              </p>
            </div>
          </div>
        )}
      </motion.div>

      {/* ========== v4.0 前事验证弹窗 ========== */}
      <PastValidationModal
        isOpen={showValidation}
        onClose={() => setShowValidation(false)}
        baziData={baziData}
        dayunData={dayunData}
        onComplete={handleValidationComplete}
      />

      {/* ========== v4.0 结束反馈弹窗 ========== */}
      {showEndFeedback && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={skipEndFeedback} />
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative w-full sm:w-[400px] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-4">
              <h3 className="text-white font-bold text-lg">感谢你的对话 🙏</h3>
              <p className="text-white/70 text-xs">希望这次解读对你有帮助</p>
            </div>

            <div className="p-5 space-y-4">
              {/* 整体评分 */}
              <div>
                <p className="text-sm text-ink font-medium mb-2">整体满意度</p>
                <div className="flex gap-2 justify-center">
                  {[1,2,3,4,5].map(star => (
                    <button
                      key={star}
                      onClick={() => setEndRating(star)}
                      className={`text-3xl transition-all ${
                        star <= endRating ? 'scale-110' : 'opacity-30'
                      }`}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
              </div>

              {/* 关注领域 */}
              <div>
                <p className="text-sm text-ink font-medium mb-2">你最关注的领域（可多选）</p>
                <div className="flex flex-wrap gap-2">
                  {['事业', '财运', '感情', '健康', '学业', '运势'].map(topic => (
                    <button
                      key={topic}
                      onClick={() => {
                        setEndTopics(prev =>
                          prev.includes(topic)
                            ? prev.filter(t => t !== topic)
                            : [...prev, topic]
                        );
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        endTopics.includes(topic)
                          ? 'bg-zhusha text-white'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>

              {/* 开放建议 */}
              <div>
                <p className="text-sm text-ink font-medium mb-2">还有什么想说的吗？</p>
                <textarea
                  value={endSuggestion}
                  onChange={(e) => setEndSuggestion(e.target.value)}
                  placeholder="任何建议或想法都可以告诉我..."
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:border-amber-300 focus:ring-1 focus:ring-amber-200 focus:outline-none"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={skipEndFeedback}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm"
                >
                  跳过
                </button>
                <button
                  onClick={handleEndFeedbackSubmit}
                  className="flex-1 py-2.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-xl text-sm font-medium"
                >
                  提交反馈
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ZhiChatModal;
